import { useFrontContext } from '@/contexts/useFrontPluginContext';
import {
  useCreateFrontReplyAllMutation,
  useSendFrontMessageMutation,
} from '@/generated/graphql';
import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useAnalytics } from '@shared/hooks/useAnalytics';

type UseReplyAllState = {
  loadingDraftReplyAll: boolean;
};

type UseReplyAllActions = {
  draftReplyAll: (itemId: string, content?: string) => Promise<void>;
};

export const useReplyAll = (): UseReplyAllState & UseReplyAllActions => {
  const { defaultOrgId } = useMeContext();
  const { conversationId, openThread, frontUser, listMessages } =
    useFrontContext();
  const { sendToast } = useToast();
  const [draftReplyAllMutation, { loading: loadingDraftReplyAll }] =
    useCreateFrontReplyAllMutation();
  const [sendMessageMutation] = useSendFrontMessageMutation();
  const { track } = useAnalytics();

  const toastNoOrgId = () => {
    sendToast('Failed to draft message', {
      description: 'Organization ID not found.',
      variant: 'error',
    });
  };

  const toastFailedDraft = () => {
    sendToast('Failed to compose message', {
      description: 'Something went wrong drafting your message.',
      variant: 'error',
    });
  };

  const toastSuccessDraft = (
    messageId: string,
    content?: string,
    originatorAddress?: string,
    channelEmail?: string,
    ccRecipients?: string[],
    bccRecipients?: string[]
  ) => {
    sendToast('Success!', {
      description: 'Your message was drafted.',
      variant: 'success',
      autoHideDuration: 7 * 1000,
      actions: [
        {
          label: 'Send message',
          onPress: () => {
            void sendMessage(
              messageId,
              content,
              originatorAddress,
              channelEmail,
              ccRecipients,
              bccRecipients
            );
          },
        },
      ],
    });
  };

  const sendMessage = async (
    messageId: string,
    content?: string,
    originatorAddress?: string,
    channelEmail?: string,
    ccRecipients?: string[],
    bccRecipients?: string[]
  ) => {
    if (!defaultOrgId) return toastNoOrgId();

    const res = await sendMessageMutation({
      variables: {
        input: {
          organizationId: defaultOrgId,
          frontUserEmail: frontUser.email,
          frontConversationId: conversationId,
          messageId,
          content,
          originatorAddress,
          channelEmail,
          ccRecipients,
          bccRecipients,
        },
      },
    });

    if (!res.data?.sendFrontMessage?.success) {
      track('Failed to Send Reply All');
      sendToast('Failed to send message', {
        description: 'Something went wrong sending your message.',
        variant: 'error',
      });
    } else {
      track('Sent Reply All');
      sendToast('Success!', {
        description: 'Your message was sent.',
        variant: 'success',
      });
    }
  };

  const openConversation = (threadId: string) => {
    if (!openThread) return;
    openThread(threadId);
  };

  const draftReplyAll = async (itemId: string, content?: string) => {
    if (!defaultOrgId) return toastNoOrgId();

    console.info('[Front Add-in] draftReplyAll called', {
      itemId,
      conversationId,
      frontUserEmail: frontUser.email,
      hasContent: Boolean(content),
      itemMatchesConversation: itemId === conversationId,
    });

    const isItemInFocus = itemId === conversationId;

    try {
      // Get messages to extract originator, channel email, and cc/bcc
      const messages = await listMessages();
      const lastMessage = messages.results[messages.results.length - 1];

      console.info('[Front Add-in] Messages retrieved', {
        messageCount: messages.results.length,
        lastMessageFrom: lastMessage?.from?.handle,
        lastMessageTo: lastMessage?.to?.map((r) => r.handle),
      });

      // originatorAddress is the sender's email
      const originatorAddress = lastMessage?.from?.handle;

      // channelEmail is the first recipient (shared inbox address if applicable)
      const channelEmail = lastMessage?.to?.[0]?.handle;

      const ccRecipients = lastMessage?.cc
        ?.map((r) => r.handle)
        .filter((email) => email !== frontUser.email);

      const bccRecipients = lastMessage?.bcc
        ?.map((r) => r.handle)
        .filter((email) => email !== frontUser.email);

      const input = {
        organizationId: defaultOrgId,
        frontUserEmail: frontUser.email,
        itemId: itemId,
        frontConversationId: conversationId,
        content,
        originatorAddress,
        channelEmail,
        ccRecipients,
        bccRecipients,
      };

      console.info('[Front Add-in] Calling createFrontReplyAll mutation', {
        input: {
          ...input,
          content: Boolean(content),
        },
      });

      const res = await draftReplyAllMutation({ variables: { input } });

      console.info('[Front Add-in] Mutation response', {
        success: res?.data?.createFrontReplyAll?.success,
        messageId: res?.data?.createFrontReplyAll?.messageId,
        errors: res?.errors,
      });

      const draftMessageId = res?.data?.createFrontReplyAll?.messageId;
      if (!res.data?.createFrontReplyAll?.success) {
        console.error('[Front Add-in] Draft creation failed');
        toastFailedDraft();
      } else if (draftMessageId && isItemInFocus) {
        toastSuccessDraft(
          draftMessageId,
          content,
          originatorAddress,
          channelEmail,
          ccRecipients,
          bccRecipients
        );
      } else if (draftMessageId) {
        openConversation(itemId);
      }
    } catch (e) {
      console.error('[Front Add-in] Draft creation threw an exception', e);
      toastFailedDraft();
    }
  };

  return { loadingDraftReplyAll, draftReplyAll };
};
